import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Search, Edit2, Trash2, Eye, EyeOff, RefreshCw,
  Package, Save, X, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { fileToResizedDataUrl } from '../utils/imageUpload';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EMPTY_LOC = {
  nom: '', description_courte: '', description_longue: '', images: [''],
  tarifs: { particulier: 0, entreprise: 0, association: 0, ce: 0, groupe: 0 },
  montant_caution: 0, caution_notes: '', conditions_location: '',
  options: [], quantite_disponible: 1, tags: '', sku: '',
  disponible: true, featured: false,
  seo: { seo_title: '', meta_description: '', slug: '', keywords: [] }, faq: [],
};

function FieldL({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>{label}</label>
      <Input type={type} placeholder={placeholder || ''} value={value || ''} onChange={onChange}
        style={{ background: 'var(--admin-surface)', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)' }} />
    </div>
  );
}

function LocationForm({ product, onSave, onCancel }) {
  const [form, setForm] = useState(() => product ? {
    ...product,
    tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
    images: product.images?.length ? product.images : [''],
  } : { ...EMPTY_LOC });
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setTarif = (k, v) => setForm(p => ({ ...p, tarifs: { ...p.tarifs, [k]: parseFloat(v) || 0 } }));
  const setSEO = (k, v) => setForm(p => ({ ...p, seo: { ...(p.seo || {}), [k]: v } }));

  function autoTarifs(base) {
    const b = parseFloat(base) || 0;
    setForm(p => ({ ...p, tarifs: { particulier: b, entreprise: parseFloat((b * 0.85).toFixed(2)), association: parseFloat((b * 0.85).toFixed(2)), ce: parseFloat((b * 0.90).toFixed(2)), groupe: parseFloat((b * 0.80).toFixed(2)) } }));
  }

  async function submit() {
    if (!form.nom.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const body = { ...form, tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags, images: (form.images || ['']).filter(Boolean) };
      const url = product ? `${BACKEND}/api/admin/catalogue/location/${product.id}` : `${BACKEND}/api/admin/catalogue/location`;
      const res = await fetch(url, { method: product ? 'PUT' : 'POST', headers: H, body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      toast.success(product ? 'Matériel mis à jour !' : 'Matériel créé !'); onSave();
    } catch { toast.error('Erreur'); }
    setSaving(false);
  }

  const TABS = [{ id: 'general', label: 'Général' }, { id: 'tarifs', label: 'Tarifs & Caution' }, { id: 'images', label: 'Photos' }, { id: 'options', label: 'Options' }, { id: 'seo', label: 'SEO' }];
  const PROFILS = [{ k: 'particulier', label: 'Particulier', color: '#9CA3AF' }, { k: 'entreprise', label: 'Entreprise', color: 'var(--admin-teal)' }, { k: 'association', label: 'Association', color: '#7DB1FF' }, { k: 'ce', label: 'CE', color: '#C58B19' }, { k: 'groupe', label: 'Partenaire', color: 'var(--admin-gold)' }];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.8)' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="w-full max-w-3xl rounded-2xl" style={{ background: 'var(--admin-bg)', border: '1px solid var(--admin-border)' }}>
          <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <h2 className="font-bold text-lg" style={{ color: 'var(--admin-teal)' }}>{product ? '✏️ Modifier' : '➕ Nouveau'} matériel location</h2>
            <button onClick={onCancel} style={{ color: 'var(--admin-muted)' }}><X size={20} /></button>
          </div>
          <div className="flex overflow-x-auto" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="px-4 py-3 text-sm font-medium flex-shrink-0"
                style={activeTab === tab.id ? { color: 'var(--admin-teal)', borderBottom: '2px solid #5DBCBF' } : { color: 'var(--admin-muted)' }}>
                {tab.label}
              </button>
            ))}
          </div>
          <div className="p-5 space-y-4" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {activeTab === 'general' && (<>
              <FieldL label="Nom du matériel *" value={form.nom} onChange={e => setF('nom', e.target.value)} />
              <FieldL label="SKU" value={form.sku} onChange={e => setF('sku', e.target.value)} placeholder="SE-LOC-001" />
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description courte</label>
                <textarea value={form.description_courte} onChange={e => setF('description_courte', e.target.value)} rows={2}
                  className="w-full px-3 py-2 rounded-xl text-sm resize-y" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Description longue SEO</label>
                <textarea value={form.description_longue} onChange={e => setF('description_longue', e.target.value)} rows={4}
                  className="w-full px-3 py-2 rounded-xl text-sm resize-y" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <FieldL label="Quantité disponible" type="number" value={form.quantite_disponible} onChange={e => setF('quantite_disponible', parseInt(e.target.value) || 1)} />
              <FieldL label="Tags" value={form.tags} onChange={e => setF('tags', e.target.value)} placeholder="jeux, mariage, extérieur" />
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Conditions de location</label>
                <textarea value={form.conditions_location} onChange={e => setF('conditions_location', e.target.value)} rows={2}
                  className="w-full px-3 py-2 rounded-xl text-sm resize-y" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
            </>)}
            {activeTab === 'tarifs' && (<>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold" style={{ color: 'var(--admin-muted)' }}>Tarifs par profil (€/jour)</p>
                {form.tarifs.particulier > 0 && (
                  <button onClick={() => autoTarifs(form.tarifs.particulier)}
                    className="text-xs px-2 py-0.5 rounded" style={{ background: 'rgba(212,175,55,0.15)', color: 'var(--admin-gold)' }}>
                    Auto -15%/-10%/-20%
                  </button>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {PROFILS.map(p => (
                  <div key={p.k}>
                    <label className="text-xs mb-1 block" style={{ color: p.color }}>{p.label}</label>
                    <input type="number" min="0" step="0.5" value={form.tarifs[p.k] || 0} onChange={e => setTarif(p.k, e.target.value)}
                      className="w-full text-sm text-center px-2 py-1.5 rounded-lg"
                      style={{ background: 'var(--admin-surface)', border: `1px solid ${p.color}30`, color: 'var(--admin-text)', outline: 'none' }} />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4 mt-3">
                <FieldL label="Montant caution (€)" type="number" value={form.montant_caution || 0} onChange={e => setF('montant_caution', parseFloat(e.target.value) || 0)} />
                <FieldL label="Notes caution" value={form.caution_notes || ''} onChange={e => setF('caution_notes', e.target.value)} placeholder="Conditions de restitution..." />
              </div>
            </>)}
            {activeTab === 'images' && (<>
              {(form.images || ['']).map((img, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <span className="text-xs" style={{ color: 'var(--admin-faint)', width: 20 }}>{i + 1}</span>
                  <Input value={img} onChange={e => { const imgs = [...(form.images || [''])]; imgs[i] = e.target.value; setF('images', imgs); }}
                    placeholder={`URL image ${i + 1}...`} style={{ background: 'var(--admin-surface)', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)', flex: 1 }} />
                  {img && <img src={img} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" onError={e => e.target.style.display = 'none'} />}
                  <button onClick={() => setF('images', (form.images || ['']).filter((_, j) => j !== i))} style={{ color: '#e53e3e' }}><X size={14} /></button>
                </div>
              ))}
              {(form.images || ['']).length < 8 && (
                <button onClick={() => setF('images', [...(form.images || ['']), ''])}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg"
                  style={{ background: 'rgba(93,188,191,0.1)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.2)' }}>
                  <Plus size={12} /> Ajouter une URL
                </button>
              )}
              <label className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg cursor-pointer w-fit"
                data-testid="upload-image-location"
                style={{ background: 'rgba(212,175,55,0.1)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)' }}>
                <Upload size={12} /> Importer depuis mon ordinateur
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0]; e.target.value = '';
                  if (!file) return;
                  if (file.size > 8 * 1024 * 1024) { toast.error('Image trop lourde (max 8 Mo)'); return; }
                  try {
                    const dataUrl = await fileToResizedDataUrl(file);
                    setF('images', [...(form.images || []).filter(Boolean), dataUrl]);
                    toast.success('Photo importée');
                  } catch (err) { toast.error(err.message || 'Import impossible'); }
                }} />
              </label>
            </>)}
            {activeTab === 'options' && (<>
              {(form.options || []).map((opt, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border-soft)' }}>
                  <div className="flex justify-between mb-2"><p className="text-xs font-semibold" style={{ color: 'var(--admin-gold)' }}>Option {i + 1}</p><button onClick={() => setF('options', (form.options || []).filter((_, j) => j !== i))} style={{ color: '#e53e3e' }}><X size={13} /></button></div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={opt.nom || ''} onChange={e => { const opts = [...(form.options || [])]; opts[i] = { ...opts[i], nom: e.target.value }; setF('options', opts); }} placeholder="Nom option" style={{ background: '#222', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)' }} />
                    <Input type="number" value={opt.prix || 0} onChange={e => { const opts = [...(form.options || [])]; opts[i] = { ...opts[i], prix: parseFloat(e.target.value) || 0 }; setF('options', opts); }} placeholder="Prix (€)" style={{ background: '#222', borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-text)' }} />
                  </div>
                </div>
              ))}
              <button onClick={() => setF('options', [...(form.options || []), { nom: '', prix: 0 }])}
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg"
                style={{ background: 'rgba(212,175,55,0.1)', color: 'var(--admin-gold)', border: '1px solid rgba(212,175,55,0.2)' }}>
                <Plus size={12} /> Ajouter une option
              </button>
            </>)}
            {activeTab === 'seo' && (<>
              <FieldL label="Titre SEO" value={form.seo?.seo_title || ''} onChange={e => setSEO('seo_title', e.target.value)} placeholder={form.nom} />
              <div>
                <label className="text-xs mb-1 block" style={{ color: 'var(--admin-muted)' }}>Meta description</label>
                <textarea value={form.seo?.meta_description || ''} onChange={e => setSEO('meta_description', e.target.value)} rows={2} maxLength={160}
                  className="w-full px-3 py-2 rounded-xl text-sm resize-y" style={{ background: 'var(--admin-surface)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)', outline: 'none' }} />
              </div>
              <FieldL label="Slug URL" value={form.seo?.slug || ''} onChange={e => setSEO('slug', e.target.value)} />
              <FieldL label="Mots-clés (virgule)" value={(form.seo?.keywords || []).join(', ')} onChange={e => setSEO('keywords', e.target.value.split(',').map(k => k.trim()).filter(Boolean))} />
            </>)}
          </div>
          <div className="flex items-center gap-4 px-5 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.disponible} onChange={e => setF('disponible', e.target.checked)} style={{ accentColor: '#1F9D7A' }} /><span className="text-xs" style={{ color: 'var(--admin-text)' }}>Visible</span></label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.featured} onChange={e => setF('featured', e.target.checked)} style={{ accentColor: '#D4AF37' }} /><span className="text-xs" style={{ color: 'var(--admin-text)' }}>Bestseller</span></label>
            <div className="flex-1" />
            <Button onClick={submit} disabled={saving} style={{ background: 'rgba(93,188,191,0.2)', color: 'var(--admin-teal)', border: '1px solid rgba(93,188,191,0.3)' }}>
              <Save size={14} className="mr-2" />{saving ? 'Sauvegarde...' : (product ? 'Mettre à jour' : 'Créer')}
            </Button>
            <Button onClick={onCancel} variant="outline" style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'var(--admin-muted)' }}>Annuler</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminCatalogueLocation() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${BACKEND}/api/admin/catalogue/location?limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const d = await fetch(url, { headers: H }).then(r => r.json());
      setProducts(d.products || []); setTotal(d.total || 0);
    } catch { toast.error('Erreur'); }
    setLoading(false);
  // eslint-disable-next-line
  }, [search]);

  useEffect(() => { load(); }, [load]);

  async function toggleDispo(p) {
    await fetch(`${BACKEND}/api/admin/catalogue/location/${p.id}`, { method: 'PUT', headers: H, body: JSON.stringify({ disponible: !p.disponible }) });
    load();
  }

  async function del(p) {
    if (!window.confirm(`Supprimer "${p.nom}" ?`)) return;
    await fetch(`${BACKEND}/api/admin/catalogue/location/${p.id}`, { method: 'DELETE', headers: H });
    toast.success('Supprimé'); load();
  }

  async function moveToType(p, newType) {
    if (!newType) return;
    await fetch(`${BACKEND}/api/products/${p.id}`, { method: 'PUT', headers: H, body: JSON.stringify({ type: newType }) });
    toast.success(`« ${p.nom} » déplacé`); load();
  }

  const DISPO_STATUS = (p) => {
    if (!p.disponible) return { label: 'Masqué',      color: 'var(--admin-red)',   bg: 'var(--admin-red-dim)' };
    return               { label: 'Disponible',       color: 'var(--admin-green)', bg: 'var(--admin-green-dim)' };
  };

  return (
    <div data-testid="admin-catalogue-location">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700,
              letterSpacing: '-.6px', color: 'var(--admin-text)' }}>Matériel de location</h2>
            <p style={{ color: 'var(--admin-muted)', fontSize: 13.5, marginTop: 3 }}>
              {total} référence{total > 1 ? 's' : ''} · 1 unité par produit
            </p>
          </div>
          <div style={{ display: 'flex', gap: 7 }}>
            <button onClick={load}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 10,
                border: '1px solid var(--admin-border)', background: 'var(--admin-surface)',
                color: 'var(--admin-muted)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
              <RefreshCw size={13}/>
            </button>
            <button onClick={() => { setEditProduct(null); setShowForm(true); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10,
                background: 'var(--admin-teal)', color: '#052b25', fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer' }}>
              <Plus size={14}/> Nouveau matériel
            </button>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={13} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-faint)' }} />
        <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 34, background: 'var(--admin-surface)', borderColor: 'var(--admin-border-soft)', color: 'var(--admin-text)', height: 38 }} />
      </div>

      {/* Liste */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1,2,3,4].map(i => <div key={i} className="adm-card" style={{ height: 72, background: 'var(--admin-surface2)' }} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {products.map(p => {
            const ds = DISPO_STATUS(p);
            return (
              <div key={p.id} className="adm-card"
                style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12,
                  opacity: p.disponible ? 1 : 0.6, transition: 'border-color .15s' }}>
                {/* Image */}
                {p.images?.[0] ? (
                  <img src={p.images[0]} alt={p.nom} loading="lazy"
                    style={{ width: 48, height: 48, borderRadius: 11, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 48, height: 48, borderRadius: 11, flexShrink: 0,
                    background: 'var(--admin-surface2)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'var(--admin-faint)', fontSize: 18 }}>📦</div>
                )}
                {/* Infos */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3, flexWrap: 'wrap' }}>
                    <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--admin-text)', margin: 0 }}>{p.nom}</p>
                    <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 7,
                      background: ds.bg, color: ds.color }}>{ds.label}</span>
                    {p.montant_caution > 0 && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 7,
                        background: 'var(--admin-gold-dim)', color: 'var(--admin-gold)' }}>🔒 {p.montant_caution}€</span>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--admin-muted)', margin: 0 }}>
                    {p.tarifs?.particulier > 0
                      ? <><strong style={{ color: 'var(--admin-text)' }}>{p.tarifs.particulier}€</strong>/jour</>
                      : 'À partir de — €'}
                    {' · 1 unité'}
                    {p.options?.length > 0 && ` · ${p.options.length} option(s)`}
                  </p>
                </div>
                {/* Actions */}
                <div style={{ display: 'flex', gap: 5, flexShrink: 0, alignItems: 'center' }}>
                  <select value="" onChange={e => moveToType(p, e.target.value)} title="Déplacer vers un autre univers"
                    data-testid={`loc-move-${p.id}`}
                    style={{ height: 32, borderRadius: 9, border: '1px solid var(--admin-border)', cursor: 'pointer',
                      background: 'var(--admin-surface2)', color: 'var(--admin-muted)', fontSize: 11, padding: '0 6px' }}>
                    <option value="">Déplacer…</option>
                    <option value="prestation">Prestation</option>
                    <option value="vente">Vente</option>
                  </select>
                  <button onClick={() => toggleDispo(p)} title={p.disponible ? 'Masquer' : 'Rendre visible'}
                    style={{ width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                      background: p.disponible ? 'var(--admin-green-dim)' : 'var(--admin-surface2)',
                      color: p.disponible ? 'var(--admin-green)' : 'var(--admin-faint)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {p.disponible ? <Eye size={13}/> : <EyeOff size={13}/>}
                  </button>
                  <button onClick={() => { setEditProduct(p); setShowForm(true); }} title="Modifier"
                    style={{ width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                      background: 'var(--admin-blue-dim)', color: 'var(--admin-blue)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Edit2 size={13}/>
                  </button>
                  <button onClick={() => del(p)} title="Supprimer"
                    style={{ width: 32, height: 32, borderRadius: 9, border: 'none', cursor: 'pointer',
                      background: 'var(--admin-red-dim)', color: 'var(--admin-red)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Trash2 size={13}/>
                  </button>
                </div>
              </div>
            );
          })}
          {products.length === 0 && !loading && (
            <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--admin-faint)' }}>
              <Package size={36} style={{ margin: '0 auto 12px', opacity: .2 }}/>
              <p>Aucun matériel trouvé</p>
            </div>
          )}
        </div>
      )}
      {showForm && <LocationForm product={editProduct} onSave={() => { setShowForm(false); setEditProduct(null); load(); }} onCancel={() => { setShowForm(false); setEditProduct(null); }} />}
    </div>
  );
}
